import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import useTranslation from '@/utils/hooks/translation/useTranslation';

dayjs.extend(utc);
dayjs.extend(timezone);

// Hooks
import { useAlbumContext } from '@/utils/hooks/context/useAlbumContext';
import useHashtags from '@/utils/hooks/swr/useHashtags';
import useLabels from '@/utils/hooks/swr/useLabels';
// Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Utils
import { post, put } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';

// Lib
import { ALBUM, ALBUMS } from '@/lib/routes/apiEndpoints';

import InfoBanner from '@/components/base/InfoBanner';
// Components
import Modal from '@/components/base/Modal';
import AlbumTagsFilter from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/AutomaticStep/AlbumTagsFilter';
import DatepickerFilter from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/AutomaticStep/DatepickerFilter';
import HashtagsFilter from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/AutomaticStep/HashtagsFilter';
import PlaceholderLoader from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/AutomaticStep/PlaceholderLoader';
import PostTypeFilter from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/AutomaticStep/PostTypeFilter';
import FooterModal from '@/components/pages/albums/AlbumFunnelModal/components/FooterModal';
import InputAlbumName from '@/components/pages/albums/AlbumFunnelModal/components/InputAlbumName';
import InstagramMediaType from '@/components/pages/albums/AlbumFunnelModal/components/InstagramMediaType';

// Routes
import { CONTENT_ALBUM_ANALYTICS } from '@/routes/privateRoutes';

export const AutomaticStep = ({ dismiss, callbackUpdateAlbum }) => {
    const router = useRouter();
    const refBannerInfo = useRef();
    const bannerInfoInViewPort = useIntersectionObserver(refBannerInfo, '0px');
    const [submitStatus, setSubmitStatus] = useState({
        isSubmitting: false,
        error: null
    });

    const { user } = useUser();
    const { albumInfo } = useAlbumContext();
    const { t } = useTranslation('content');
    const { activeSocialProfile } = useActiveSocialProfile();

    const { hashtags, isLoading: isLoadingHashtags } = useHashtags({
        activeSPID: activeSocialProfile?.id,
        options: {
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    });

    const {
        labels: labelsOptions,
        isLoading: isLoadingLabels,
        requestParams,
        setRequestParams
    } = useLabels({
        activeSPID: activeSocialProfile?.id,
        options: {
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    });

    useEffect(() => {
        if (albumInfo.criteria?.album_type) {
            setRequestParams({ media_type: 'stories' });
        }
        if (!albumInfo.criteria?.album_type && requestParams?.media_type) {
            setRequestParams(undefined);
        }
    }, [albumInfo.criteria?.album_type]);

    const submitAutomaticAlbum = async () => {
        setSubmitStatus((prevState) => ({
            ...prevState,
            isSubmitting: true,
            error: null
        }));
        try {
            const formatDate = (dateToFormat) => {
                if (!dateToFormat) return null;
                return dayjs.tz(dateToFormat, user.settings.timezone).toISOString();
            };
            const dataToSend = {
                name: albumInfo?.name,
                type: albumInfo?.type,
                criteria: {
                    ...albumInfo?.criteria,
                    started_at: formatDate(albumInfo?.criteria?.started_at),
                    ended_at: formatDate(albumInfo?.criteria?.ended_at)
                }
            };
            if (albumInfo?.id) {
                await put(
                    injectEndpointUrlParams(ALBUM, {
                        SPID: activeSocialProfile?.id,
                        ALBUM_ID: albumInfo?.id
                    }),
                    dataToSend
                );

                if (typeof callbackUpdateAlbum === 'function') {
                    await callbackUpdateAlbum();
                }

                if (typeof dismiss === 'function') {
                    dismiss();
                }
            } else {
                const { data } = await post(
                    ALBUMS.replace('%SPID%', activeSocialProfile?.id),
                    dataToSend
                );
                router.push(CONTENT_ALBUM_ANALYTICS.replace('[albumId]', data?.data?.id));
            }
        } catch (e) {
            if (e?.response?.data?.errors?.name?.includes('unique')) {
                setSubmitStatus((prevState) => ({
                    ...prevState,
                    isSubmitting: false,
                    error: t('content:modal.update_album_error_name_unique')
                }));
            } else {
                setSubmitStatus((prevState) => ({
                    ...prevState,
                    isSubmitting: false,
                    error: t('errors:error_occured_submit_data')
                }));
            }

            // Scroll To info banner if it's not visible in the viewport
            if (!bannerInfoInViewPort) {
                refBannerInfo?.current?.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    return (
        <div className="w-[calc(100vw-24px)] sm:max-w-[37.5rem]">
            <Modal.Body>
                <div ref={refBannerInfo} />
                {submitStatus?.error ? (
                    <InfoBanner
                        className="mb-4"
                        title={t('errors:error')}
                        message={submitStatus?.error}
                        onClose={() => {
                            setSubmitStatus((prevState) => ({
                                ...prevState,
                                error: null
                            }));
                        }}
                    />
                ) : null}
                <Modal.Title className="mb-8 text-center">
                    {albumInfo?.id
                        ? t('modal.edit_modal.title')
                        : t('modal.create_modal.title_create_auto')}
                </Modal.Title>
                {albumInfo?.id ? (
                    <div className="mb-6">
                        <InputAlbumName />
                    </div>
                ) : null}
                <Modal.Content className="rounded-md bg-icoGray-100 dark:bg-icoDarkMode-deepBlack">
                    {isLoadingHashtags ? (
                        <PlaceholderLoader />
                    ) : (
                        <div>
                            <InstagramMediaType />
                            <div className="space-y-8 p-5 sm:p-8">
                                <PostTypeFilter />
                                <HashtagsFilter hashtags={hashtags} />
                                <AlbumTagsFilter
                                    labelsOptions={labelsOptions}
                                    setRequestParams={setRequestParams}
                                />
                                <DatepickerFilter />
                            </div>
                        </div>
                    )}
                </Modal.Content>
            </Modal.Body>
            <FooterModal
                disableSubmitButton={isLoadingHashtags || isLoadingLabels}
                submitStatus={submitStatus}
                submitAlbum={submitAutomaticAlbum}
            />
        </div>
    );
};
